/**
 * The platform's public documents — the home page, Privacy Policy and Terms
 * of Service Google's OAuth verification reads.
 *
 * They are the same on every host: no login, no tenant, no brand. Each one is
 * prerendered to `dist/<page>.html` at build (vite.config.mts), so its text is
 * in the HTTP response before any script runs; nginx serves that file ahead
 * of the SPA fallback. On a vanity domain the slug-hiding history passes these
 * paths through untouched (vanityHistory.ts) instead of reading them as
 * `/<tenant>/privacy`, and BrandResolver leaves the platform look alone.
 *
 * Adding a page here: also add it to the backend reserved slugs and to BOTH
 * maps in index.html's inline script (`reserved` and `publicDoc`).
 */

export const PUBLIC_LEGAL_PAGES = ['about', 'privacy', 'terms'] as const;

/**
 * Whether the documents are PUBLISHED. Off until the operator says so
 * (2026-10-18: the company details are not final, and nothing about the
 * company or the platform is to be readable or indexable before then). While
 * off, the build writes no page (nginx then answers 404), the app has no
 * route for them, and no link to them is shown. Turning it on is this one line.
 */
export const PUBLIC_DOCUMENTS_PUBLISHED = false;

export type PublicLegalPage = (typeof PUBLIC_LEGAL_PAGES)[number];

const PATHS = new Set<string>(PUBLIC_LEGAL_PAGES.map((page) => `/${page}`));

/** True for `/about`, `/privacy`, `/terms` (a trailing slash is the same page). */
export function isPublicLegalPath(pathname: string): boolean {
  return PATHS.has(pathname.replace(/\/+$/, ''));
}
